import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import DeliveryNotification from '../components/DeliveryNotification';

const Home = () => {
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [postalCode, setPostalCode] = useState('');
  const [deliveryAvailable, setDeliveryAvailable] = useState(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  
  // State pour la recherche et les produits
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(true);

  // ✅ Ref pour les animations au scroll
  const sectionRefs = {
    hero: useRef(null),
    icons: useRef(null),
    delivery: useRef(null),
    products: useRef(null),
    testimonials: useRef(null),
    howItWorks: useRef(null),
    faq: useRef(null),
  };

  // ✅ State pour suivre les sections visibles
  const [visibleSections, setVisibleSections] = useState({});

  // ✅ Observer les sections pour les animations au scroll
  useEffect(() => {
    const observers = {};
    const sectionIds = ['hero', 'icons', 'delivery', 'products', 'testimonials', 'howItWorks', 'faq'];

    sectionIds.forEach((id) => {
      const element = sectionRefs[id].current;
      if (element) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                setVisibleSections((prev) => ({ ...prev, [id]: true }));
              }
            });
          },
          { 
            threshold: 0.15,
            rootMargin: '0px 0px -50px 0px'
          }
        );
        observer.observe(element);
        observers[id] = observer;
      }
    });

    return () => {
      sectionIds.forEach((id) => {
        if (observers[id]) {
          observers[id].disconnect();
        }
      });
    };
  }, []);

  // Récupérer les produits depuis l'API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const response = await api.get('/products');
        if (Array.isArray(response.data)) {
          setProducts(response.data);
        } else {
          console.error('Les données reçues ne sont pas un tableau:', response.data);
          setProducts([]);
        }
      } catch (error) {
        console.error('Erreur chargement produits:', error);
        setProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, []);

  const testimonials = [
    {
      name: "Sophie Martin",
      location: "Neuilly sur seine",
      rating: 5,
      comment: "Service exceptionnel ! Livraison ponctuelle et produits de qualité. Je recommande vivement Aquaexpress.",
      image: "https://randomuser.me/api/portraits/women/1.jpg"
    },
    {
      name: "Thomas Bernard",
      location: "Neuilly-sur-Seine",
      rating: 5,
      comment: "Une véritable expérience  Les eaux sont délicieuses et le service client est réactif.",
      image: "https://randomuser.me/api/portraits/men/2.jpg"
    },
    {
      name: "Isabelle Dubois",
      location: "Boulogne-Billancourt",
      rating: 5,
      comment: "Je suis ravie. Livraison programmée et produits toujours frais.",
      image: "https://randomuser.me/api/portraits/women/3.jpg"
    }
  ];

  const Icons = {
    Quality: () => (
      <svg className="w-8 h-8 sm:w-10 sm:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3" />
      </svg>
    ),
    Delivery: () => (
      <svg className="w-8 h-8 sm:w-10 sm:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 17h2M19 17h2M12 21v-4M8 3h8M12 3v2" />
        <circle cx="8" cy="17" r="2" stroke="currentColor" strokeWidth={1.5} fill="none" />
        <circle cx="16" cy="17" r="2" stroke="currentColor" strokeWidth={1.5} fill="none" />
      </svg>
    ),
    Premium: () => (
      <svg className="w-8 h-8 sm:w-10 sm:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
      </svg>
    ),
    Stock: () => (
      <svg className="w-8 h-8 sm:w-10 sm:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 11l8-4-8-4-8 4 8 4z" />
      </svg>
    ),
    Water: () => (
      <svg className="w-12 h-12 sm:w-16 sm:h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.2} d="M12 2v2M12 4c-3.5 0-6 2.5-6 6v8h12v-8c0-3.5-2.5-6-6-6z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.2} d="M9 12h6M12 9v6" />
      </svg>
    ),
    Soda: () => (
      <svg className="w-12 h-12 sm:w-16 sm:h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.2} d="M5 3h14v3H5z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.2} d="M7 6v10c0 2.5 2 4 5 4s5-1.5 5-4V6" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.2} d="M9 12h6" />
      </svg>
    ),
    Milk: () => (
      <svg className="w-12 h-12 sm:w-16 sm:h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.2} d="M7 3h10l1 5H6z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.2} d="M8 8v9c0 2 1 3 4 3s4-1 4-3V8" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.2} d="M10 12h4" />
      </svg>
    ),
    MapMarker: () => (
      <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    Calendar: () => (
      <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    Cart: () => (
      <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-1.5 6M17 13l1.5 6M9 21h6" />
      </svg>
    ),
    Star: () => (
      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ),
    ChevronLeft: () => (
      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
    ),
    ChevronRight: () => (
      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    ),
    Search: () => (
      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    )
  };

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const checkDelivery = () => {
    const availableZones = ['93360', '93330', '93220', '93250', '93110', '93100', '93160', '93340', '94170', '94130', '94360', '94500', '94120', '94300', '94160', '77500'];
    setDeliveryAvailable(availableZones.includes(postalCode));
  };

  // Filtrer les produits selon la recherche
  const filteredProducts = (Array.isArray(products) ? products : []).filter(product => {
    const matchSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        (product.brand && product.brand.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchSearch;
  });

  // Limiter l'affichage à 4 produits maximum sur l'accueil
  const displayProducts = filteredProducts.slice(0, 4);

  // Fonction d'ajout au panier
  const handleAddToCart = (product, quantity) => {
    if (!user) {
      sessionStorage.setItem('redirectAfterLogin', '/');
      toast.error('Veuillez vous connecter pour ajouter des produits au panier');
      navigate('/login');
      return;
    }
    addToCart({ ...product, quantity });
    toast.success(`${product.name} ajouté au panier !`);
  };

  useEffect(() => {
    const interval = setInterval(nextTestimonial, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div>
      {/* ✅ Hero Section - Image pleine largeur + bouton en bas */}
      <div 
        ref={sectionRefs.hero}
        className={`relative h-[300px] sm:h-[420px] md:h-[550px] lg:h-[650px] xl:h-[750px] w-full overflow-hidden transition-all duration-1000 ${
          visibleSections.hero ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
      >
        <img 
          src="https://i.pinimg.com/1200x/58/39/ec/5839ec16e591bae0dea7aa9f9609ac47.jpg" 
          alt="Livraison de boissons à domicile" 
          className="w-full h-full object-cover object-center"
        />
        
        {/* ✅ Contenu positionné en bas à droite */}
        <div className="absolute inset-x-0 bottom-3 sm:bottom-4 md:bottom-6 lg:bottom-8 xl:bottom-10 z-20 flex justify-end px-3 sm:px-6 md:px-10 lg:px-16 xl:px-20">
          <div className="w-full max-w-3xl text-right text-white">
            <div className="animate-fade-in-up animation-delay-400">
              <Link 
                to="/products" 
                className="inline-flex items-center gap-2 sm:gap-3 bg-secondary text-primary px-5 sm:px-7 md:px-10 lg:px-12 py-2 sm:py-2.5 md:py-3.5 lg:py-4.5 rounded-full font-semibold hover:scale-105 transition-all duration-300 text-sm sm:text-base md:text-lg xl:text-xl shadow-lg hover:shadow-2xl"
              >
                Commander <Icons.Cart />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Section 3 icônes - Animation slide-up */}
      <div 
        ref={sectionRefs.icons}
        className={`py-8 sm:py-16 md:py-20 bg-pearl transition-all duration-1000 ${
          visibleSections.icons ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}
      >
        <div className="container-custom">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="text-center group hover:scale-105 transition-transform duration-300">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:bg-primary group-hover:text-white transition text-primary">
                <Icons.Quality />
              </div>
              <h3 className="text-base sm:text-xl font-bold mb-2">Tous vos produits</h3>
              <p className="text-sm sm:text-base text-gray-600 px-2">Retrouvez toutes vos boissons : eaux, sodas, jus de fruits et laits.</p>
            </div>
            
            <div className="text-center group hover:scale-105 transition-transform duration-300 delay-100">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:bg-primary group-hover:text-white transition text-primary">
                <Icons.Delivery />
              </div>
              <h3 className="text-base sm:text-xl font-bold mb-2">Livraison Rapide</h3>
              <p className="text-sm sm:text-base text-gray-600 px-2">Commandez en quelques clics et recevez vos boissons directement à domicile.</p>
            </div>

            <div className="text-center group hover:scale-105 transition-transform duration-300 delay-200 sm:col-span-2 md:col-span-1">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:bg-primary group-hover:text-white transition text-primary">
                <Icons.Stock />
              </div>
              <h3 className="text-base sm:text-xl font-bold mb-2">Faites votre stock</h3>
              <p className="text-sm sm:text-base text-gray-600 px-2">Vous pouvez faire un stock sans bouger de chez vous.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Section Vérification zone - Animation slide-up */}
      <div 
        ref={sectionRefs.delivery}
        className={`py-10 sm:py-16 md:py-20 bg-cream transition-all duration-1000 ${
          visibleSections.delivery ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}
      >
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Livraison à votre Porte</h2>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">Vérifiez la disponibilité de la livraison dans votre ville</p>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-3 sm:mb-4">
                <input
                  type="text"
                  placeholder="Code postal"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-full focus:outline-none focus:border-secondary text-sm sm:text-base transition-all duration-300 focus:scale-105"
                />
                <button onClick={checkDelivery} className="bg-primary text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-full hover:bg-opacity-90 transition text-sm sm:text-base hover:scale-105">
                  Vérifier
                </button>
              </div>
              {deliveryAvailable !== null && (
                <div className="mt-2 sm:mt-4">
                  {deliveryAvailable ? (
                    <p className="text-sm sm:text-base text-green-600 animate-fade-in">
                      ✓ Bonne nouvelle ! Nous livrons dans votre zone
                    </p>
                  ) : (
                    <DeliveryNotification 
                      postalCode={postalCode}
                      onClose={() => setDeliveryAvailable(null)}
                    />
                  )}
                </div>
              )}
              <p className="text-xs sm:text-sm text-gray-400 mt-2 sm:mt-4">Livraison en moins de 24h.</p>
            </div>
            <div className="relative order-first md:order-last hover:scale-105 transition-transform duration-500">
              <img 
                src="https://i.pinimg.com/736x/ca/14/80/ca14804ac8227ff2f9d1e563edd2136d.jpg" 
                alt="Carte de Ile France"
                className="rounded-2xl shadow-lg w-full max-h-[250px] sm:max-h-[350px] object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-white/90 backdrop-blur rounded-full p-2 sm:p-3 shadow-lg text-secondary animate-pulse">
                  <Icons.MapMarker />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Section Nos Produits - Animation slide-up */}
      <div 
        ref={sectionRefs.products}
        className={`py-10 sm:py-16 md:py-20 bg-pearl transition-all duration-1000 ${
          visibleSections.products ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}
      >
        <div className="container-custom">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-2 sm:mb-4">Nos Produits</h2>
          <p className="text-sm sm:text-base text-center text-gray-600 mb-6 sm:mb-8">Trouvez vos boissons produits préférées</p>
          
          {/* Barre de recherche */}
          <div className="max-w-md mx-auto mb-8 sm:mb-10">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher un produit..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-10 border border-gray-200 rounded-full text-sm focus:outline-none focus:border-secondary shadow-sm transition-all duration-300 focus:scale-105 focus:shadow-lg"
              />
              <div className="absolute left-3.5 top-3.5">
                <Icons.Search />
              </div>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3.5 top-3 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  ✕
                </button>
              )}
            </div>
            {searchTerm && (
              <p className="text-xs text-gray-500 mt-2 text-center animate-fade-in">
                {filteredProducts.length} résultat{filteredProducts.length > 1 ? 's' : ''} trouvé{filteredProducts.length > 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* 3 CADRES : Eaux, Sodas, Laits - Sans couche sombre */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-10">
            <Link to="/products?category=eaux" className="group relative h-48 sm:h-64 md:h-96 rounded-2xl overflow-hidden shadow-lg hover:scale-105 transition-transform duration-500 hover:shadow-2xl">
              <img src="https://img.20mn.fr/hatjCxbwRDqAsPn6Wn38zg/1444x920_des-packs-de-bouteilles-d-eau-cristaline-illustration" className="w-full h-full object-cover group-hover:scale-110 transition duration-700" />
              <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 text-white">
                <div className="flex justify-center"><Icons.Water /></div>
                <h3 className="text-lg sm:text-2xl font-bold mt-1 sm:mt-2">Eaux</h3>
                <p className="text-xs sm:text-sm group-hover:translate-x-2 transition-transform duration-300 text-white/80">Découvrir →</p>
              </div>
            </Link>

            <Link to="/products?category=sodas" className="group relative h-48 sm:h-64 md:h-96 rounded-2xl overflow-hidden shadow-lg hover:scale-105 transition-transform duration-500 delay-100 hover:shadow-2xl">
              <img src="https://i.pinimg.com/1200x/a3/a5/f7/a3a5f7ef562c3df0bb86be9625e9a364.jpg" alt="Sodas" className="w-full h-full object-cover group-hover:scale-110 transition duration-700" />
              <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 text-white">
                <div className="flex justify-center"><Icons.Soda /></div>
                <h3 className="text-lg sm:text-2xl font-bold mt-1 sm:mt-2">Sodas et Jus de Fruit</h3>
                <p className="text-xs sm:text-sm group-hover:translate-x-2 transition-transform duration-300 text-white/80">Découvrir →</p>
              </div>
            </Link>

            <Link to="/products?category=laits" className="group relative h-48 sm:h-64 md:h-96 rounded-2xl overflow-hidden shadow-lg hover:scale-105 transition-transform duration-500 delay-200 hover:shadow-2xl sm:col-span-2 md:col-span-1">
              <img src="https://www.kirene-groupe.com/wp-content/uploads/2024/09/MOBILE-CANDIA-CLASSIQUE-1.png" alt="Laits" className="w-full h-full object-cover group-hover:scale-110 transition duration-700" />
              <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 text-white">
                <div className="flex justify-center"><Icons.Milk /></div>
                <h3 className="text-lg sm:text-2xl font-bold mt-1 sm:mt-2">Laits</h3>
                <p className="text-xs sm:text-sm group-hover:translate-x-2 transition-transform duration-300 text-white/80">Découvrir →</p>
              </div>
            </Link>
          </div>

          {/* Résultats de recherche */}
          {searchTerm && (
            <>
              {loadingProducts ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-2 text-gray-500 text-sm">Chargement des produits...</p>
                </div>
              ) : (
                <>
                  {displayProducts.length === 0 ? (
                    <div className="text-center py-12 animate-fade-in">
                      <p className="text-gray-500 text-sm">Aucun produit trouvé</p>
                      <button
                        onClick={() => setSearchTerm('')}
                        className="mt-2 text-secondary hover:underline text-sm"
                      >
                        Voir tous les produits
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
                      {displayProducts.map((product, index) => (
                        <div key={product.id} className="animate-fade-in-up" style={{ animationDelay: `${index * 100}ms` }}>
                          <HomeProductCard 
                            product={product} 
                            user={user}
                            onAddToCart={handleAddToCart}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Lien vers la boutique */}
          <div className="text-center mt-8">
            <Link to="/products" className="inline-flex items-center gap-2 bg-primary text-white px-6 py-2.5 rounded-full hover:bg-opacity-90 transition text-sm font-medium hover:scale-105 hover:shadow-lg duration-300">
              Voir tous nos produits
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* ✅ Section Avis Clients - Animation slide-up */}
      <div 
        ref={sectionRefs.testimonials}
        className={`py-10 sm:py-16 md:py-20 bg-cream transition-all duration-1000 ${
          visibleSections.testimonials ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}
      >
        <div className="container-custom">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-2 sm:mb-4">Ce que nos clients disent</h2>
          <p className="text-sm sm:text-base text-center text-gray-600 mb-6 sm:mb-10 md:mb-12">Plus de 1000 clients satisfaits</p>
          
          <div className="relative max-w-3xl mx-auto px-4 sm:px-0">
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center transition-all duration-500 hover:shadow-2xl hover:scale-105">
              <div className="flex justify-center mb-3 sm:mb-4 text-secondary">
                {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                  <Icons.Star key={i} />
                ))}
              </div>
              <p className="text-sm sm:text-lg text-gray-600 italic mb-4 sm:mb-6 transition-opacity duration-300">"{testimonials[currentTestimonial].comment}"</p>
              <div className="flex items-center justify-center gap-3 sm:gap-4">
                <img src={testimonials[currentTestimonial].image} alt={testimonials[currentTestimonial].name} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover ring-2 ring-secondary transition-all duration-300 hover:scale-110" />
                <div className="text-left">
                  <h4 className="font-bold text-sm sm:text-base">{testimonials[currentTestimonial].name}</h4>
                  <p className="text-xs sm:text-sm text-gray-500">{testimonials[currentTestimonial].location}</p>
                </div>
              </div>
            </div>
            
            <button onClick={prevTestimonial} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-12 bg-white shadow-lg p-2 sm:p-3 rounded-full hover:bg-gray-100 text-gray-600 transition-all duration-300 hover:scale-110 hover:shadow-xl">
              <Icons.ChevronLeft />
            </button>
            <button onClick={nextTestimonial} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-12 bg-white shadow-lg p-2 sm:p-3 rounded-full hover:bg-gray-100 text-gray-600 transition-all duration-300 hover:scale-110 hover:shadow-xl">
              <Icons.ChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* ✅ Section Comment ça marche - Animation slide-up */}
      <div 
        ref={sectionRefs.howItWorks}
        className={`py-10 sm:py-16 md:py-20 bg-white transition-all duration-1000 ${
          visibleSections.howItWorks ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}
      >
        <div className="container-custom">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3 sm:mb-4 text-primary">Comment ça marche ?</h2>
          <p className="text-sm sm:text-base text-center text-gray-600 mb-6 sm:mb-10 md:mb-12 px-2">
            Sélectionnez vos marques préférées et faites vous livrer en moins de 24H ou programmez une livraison selon un jour précis de votre choix !
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {[
              { step: '1', title: 'Sélectionnez vos produits', desc: 'Choisissez vos packs d\'eaux et boissons', icon: <Icons.Cart /> },
              { step: '2', title: 'Choisissez votre créneau', desc: 'Indiquez le jour et l\'horaire de livraison qui vous conviennent', icon: <Icons.Calendar /> },
              { step: '3', title: 'On livre chez vous', desc: 'Livraison à domicile, dépôt possible en cas d\'absence', icon: <Icons.Delivery /> }
            ].map((item, index) => (
              <div key={index} className="text-center group hover:scale-105 transition-transform duration-500">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 text-primary group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                  {item.icon}
                </div>
                <h3 className="text-base sm:text-xl font-bold mb-2 text-primary">{item.step}. {item.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 px-2">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ✅ Section FAQ - Animation slide-up */}
      <div 
        ref={sectionRefs.faq}
        className={`py-10 sm:py-16 md:py-20 bg-pearl transition-all duration-1000 ${
          visibleSections.faq ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}
      >
        <div className="container-custom">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-6 sm:mb-10 md:mb-12">Questions fréquentes</h2>
          <div className="max-w-3xl mx-auto space-y-3 sm:space-y-4 px-2 sm:px-0">
            {[
              { q: 'Quelles sont les zones de livraison ?', a: 'Entrez votre code postal sur la page d\'accueil pour vérifier l\'éligibilité.' },
              { q: 'Quel est le délai de livraison ?', a: 'Livraison en moins de 24H. Vous pouvez également programmer une livraison à la date de votre choix.' },
              { q: 'Y a-t-il des frais de livraison ?', a: 'Les frais de livraison sont calculés en fonction du nombre de packs commandés, à partir de 9,90€.' },
              { q: 'Puis-je modifier ma commande ?', a: 'Oui, jusqu\'à la veille de la livraison. Contactez notre service client.' }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4 sm:p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105">
                <h3 className="font-bold text-sm sm:text-lg mb-1 sm:mb-2 text-primary">{item.q}</h3>
                <p className="text-sm sm:text-base text-gray-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant HomeProductCard
const HomeProductCard = ({ product, user, onAddToCart }) => {
  const [quantity, setQuantity] = useState(1);
  const navigate = useNavigate();

  const handleAddToCart = () => {
    onAddToCart(product, quantity);
  };

  return (
    <div className="bg-white rounded-lg sm:rounded-xl shadow-md overflow-hidden hover:shadow-2xl transition-all duration-500 hover:scale-105 group">
      <Link to={`/products/${product.id}`} className="block relative overflow-hidden h-32 sm:h-40 md:h-48">
        {product.image_url ? (
          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition duration-700" />
        ) : (
          <div className="w-full h-full bg-gray-100 flex items-center justify-center">
            <svg className="w-8 h-8 sm:w-12 sm:h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        {product.old_price && (
          <span className="absolute top-1 left-1 sm:top-2 sm:left-2 bg-red-500 text-white text-[9px] sm:text-xs px-1.5 py-0.5 rounded-full animate-pulse">Soldes</span>
        )}
        {product.is_premium && !product.old_price && (
          <span className="absolute top-1 left-1 sm:top-2 sm:left-2 bg-secondary text-primary text-[9px] sm:text-xs px-1.5 py-0.5 rounded-full font-semibold">Premium</span>
        )}
        {product.is_best_seller && (
          <span className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-primary text-white text-[9px] sm:text-xs px-1.5 py-0.5 rounded-full font-semibold animate-pulse">⭐ Best</span>
        )}
      </Link>
      <div className="p-2 sm:p-3 md:p-4">
        <h3 className="font-bold text-xs sm:text-sm md:text-base line-clamp-2">{product.name}</h3>
        {product.brand && <p className="text-gray-500 text-[10px] sm:text-xs mb-0.5">{product.brand}</p>}
        <p className="text-gray-400 text-[10px] sm:text-xs mb-1">{product.format || ''}</p>
        <div className="flex items-center gap-1 sm:gap-2 mb-1.5 sm:mb-2">
          {product.old_price && (
            <span className="text-gray-400 line-through text-[10px] sm:text-xs">{product.old_price} €</span>
          )}
          <span className="text-primary font-bold text-xs sm:text-sm md:text-base">{product.price} €</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="flex items-center border rounded-lg text-xs">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="px-1.5 sm:px-2 py-0.5 sm:py-1 hover:bg-gray-100 transition-colors duration-200"
            >
              -
            </button>
            <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 min-w-[24px] sm:min-w-[32px] text-center">{quantity}</span>
            <button
              onClick={() => setQuantity(quantity + 1)}
              className="px-1.5 sm:px-2 py-0.5 sm:py-1 hover:bg-gray-100 transition-colors duration-200"
            >
              +
            </button>
          </div>
          <button
            onClick={handleAddToCart}
            className="flex-1 bg-primary text-white py-1 sm:py-1.5 rounded-lg hover:bg-opacity-90 transition text-[10px] sm:text-xs font-medium hover:scale-105 duration-300"
          >
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
};

export default Home;